<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
} from 'devextreme-vue/data-grid';
import DxTabPanel, { DxItem as DxTabItem } from 'devextreme-vue/tab-panel';
import { DxDateBox } from 'devextreme-vue/date-box';
import { DxButton } from 'devextreme-vue/button';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { bottoGridPaging, showError } from '@botto/core';
import CdpPageShell from '../components/CdpPageShell.vue';
import {
  fetchGoalDetailStats,
  fetchGoals,
  fetchGoalStats,
  type Goal,
  type GoalStatsItem,
} from '../api';

const { paging, pager } = bottoGridPaging();
const { paging: paging50, pager: pager50 } = bottoGridPaging({ pageSize: 50 });

const loading = ref(false);
const goals = ref<Goal[]>([]);
const totalStats = ref<GoalStatsItem[]>([]);
const byDateRows = ref<{ date: string; goal: string; unique_contacts: number }[]>([]);

const detailGoal = ref('');
const detailStart = ref<Date | null>(null);
const detailEnd = ref<Date | null>(null);
const detailStats = ref<{ date: string; unique_contacts: number }[]>([]);
const detailLoading = ref(false);

async function loadAll() {
  loading.value = true;
  try {
    const [goalsList, stats] = await Promise.all([fetchGoals(), fetchGoalStats()]);
    goals.value = goalsList;
    totalStats.value = stats.total ?? [];
    const rows: { date: string; goal: string; unique_contacts: number }[] = [];
    for (const day of stats.by_date ?? []) {
      const dateKey = day.date as string;
      for (const g of (day.goals as { goal: string; unique_contacts: number }[]) ?? []) {
        rows.push({
          date: dateKey,
          goal: g.goal,
          unique_contacts: g.unique_contacts,
        });
      }
    }
    byDateRows.value = rows;
  } catch (e) {
    showError(e);
  } finally {
    loading.value = false;
  }
}

async function loadDetailStats() {
  if (!detailGoal.value.trim()) {
    showError(new Error('Укажите goal'));
    return;
  }
  detailLoading.value = true;
  try {
    const res = await fetchGoalDetailStats(
      detailGoal.value.trim(),
      detailStart.value?.toISOString(),
      detailEnd.value?.toISOString(),
    );
    detailStats.value = (res.daily_stats ?? []).map((s) => ({
      date: s.date,
      unique_contacts: s.unique_contacts,
    }));
  } catch (e) {
    showError(e);
  } finally {
    detailLoading.value = false;
  }
}

onMounted(() => {
  void loadAll();
});
</script>

<template>
  <CdpPageShell title="Типы событий" :require-project="false">
    <p class="event-types-hint">
      Статистика по goals глобальная (API не фильтрует по project_id).
    </p>

    <DxLoadPanel :visible="loading" :shading="true" />

    <DxTabPanel>
      <DxTabItem title="Список goals">
        <DxDataGrid
          :data-source="goals"
          :show-borders="true"
          :column-auto-width="true"
          :paging="paging"
          :pager="pager"
          height="calc(100vh - 280px)"
        >
          <DxScrolling mode="standard" />
          <DxColumn data-field="goal" caption="Goal" />
          <DxColumn data-field="created_at" caption="Создан" data-type="datetime" width="180" />
        </DxDataGrid>
      </DxTabItem>

      <DxTabItem title="Общая статистика">
        <DxDataGrid
          :data-source="totalStats"
          :show-borders="true"
          :paging="paging"
          :pager="pager"
          height="calc(100vh - 280px)"
        >
          <DxScrolling mode="standard" />
          <DxColumn data-field="goal" caption="Goal" />
          <DxColumn data-field="unique_contacts" caption="Уникальных контактов" data-type="number" />
        </DxDataGrid>
      </DxTabItem>

      <DxTabItem title="По датам (30 дней)">
        <DxDataGrid
          :data-source="byDateRows"
          :show-borders="true"
          :paging="paging50"
          :pager="pager50"
          height="calc(100vh - 280px)"
        >
          <DxScrolling mode="standard" />
          <DxColumn data-field="date" caption="Дата" width="120" />
          <DxColumn data-field="goal" caption="Goal" />
          <DxColumn data-field="unique_contacts" caption="Уникальных контактов" data-type="number" />
        </DxDataGrid>
      </DxTabItem>

      <DxTabItem title="Статистика по goal">
        <div class="detail-filters">
          <input v-model="detailGoal" class="detail-filters__input" placeholder="goal" />
          <label>
            С
            <DxDateBox v-model:value="detailStart" type="date" />
          </label>
          <label>
            По
            <DxDateBox v-model:value="detailEnd" type="date" />
          </label>
          <DxButton text="Загрузить" type="default" @click="loadDetailStats" />
        </div>
        <DxLoadPanel :visible="detailLoading" :shading="true" />
        <DxDataGrid
          :data-source="detailStats"
          :show-borders="true"
          :paging="paging"
          :pager="pager"
          height="400"
        >
          <DxScrolling mode="standard" />
          <DxColumn data-field="date" caption="Дата" data-type="datetime" width="180" />
          <DxColumn data-field="unique_contacts" caption="Уникальных контактов" data-type="number" />
        </DxDataGrid>
      </DxTabItem>
    </DxTabPanel>
  </CdpPageShell>
</template>

<style scoped>
.event-types-hint {
  margin: 0 0 12px;
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
}
.detail-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 12px;
  padding-top: 8px;
}
.detail-filters label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.875rem;
}
.detail-filters__input {
  padding: 8px 10px;
  min-width: 160px;
}
</style>
